import { Link } from "react-router-dom"
import {
  Calendar,
  Phone,
  MapPin,
  Clock,
  Building,
  CheckCircle,
  Truck,
} from "lucide-react"
import SEO from "../SEO"
import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { Badge } from "../../components/ui/badge"

export default function IsnapurBranch() {
  const isnapurSchema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalOrganization",
    "name": "Pranaam Hospitals – Isnapur",
    "url": "https://pranaam.pranaamhospitals.in/locations/isnapur",
    "logo": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png",
    "description": "Pranaam Hospitals Isnapur is a NABH-accredited multi-specialty hospital offering comprehensive healthcare, advanced diagnostics, and emergency services near Patancheru, Hyderabad.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Isnapur",
      "addressRegion": "Hyderabad",
      "addressCountry": "IN"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "appointments",
        "telephone": "+91-99660-60000",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      },
      {
        "@type": "ContactPoint",
        "contactType": "emergency",
        "telephone": "+91-98485-98285",
        "availableLanguage": ["English", "Telugu", "Hindi"]
      }
    ],
    "parentOrganization": {
      "@type": "MedicalOrganization",
      "name": "Pranaam Hospitals",
      "url": "https://pranaam.pranaamhospitals.in/"
    }
  },

  {
    "@context": "https://schema.org",
    "@type": "Article",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://pranaam.pranaamhospitals.in/locations/isnapur"
    },
    "headline": "Pranaam Hospitals Isnapur",
    "description": "Visit Pranaam Hospitals Isnapur for expert medical care, specialist consultations, advanced diagnostics, and emergency services in the Hyderabad region near Patancheru.",
    "author": {
      "@type": "Organization",
      "name": "Pranaam Hospitals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Pranaam Hospitals",
      "logo": {
        "@type": "ImageObject",
        "url": "https://pranaam.pranaamhospitals.in/wp-content/uploads/2025/logo.png"
      }
    },
    "datePublished": "2015-01-01",
    "dateModified": "2025-04-01",
    "keywords": [
      "Pranaam Hospitals Isnapur",
      "hospital near Patancheru",
      "emergency care Isnapur",
      "multi specialty hospital Hyderabad"
    ]
  }
];

  return (
    <>
    <SEO
  title="Pranaam Hospitals Isnapur | Trusted Multi-Specialty Hospital in Patancheru"
  description="Pranaam Hospitals Isnapur offers quality healthcare services, advanced diagnostics, and 24/7 emergency care near Patancheru, Hyderabad. Trusted multi-specialty hospital."
  canonical="https://pranaam.pranaamhospitals.in/locations/isnapur"
  robots="index, follow"
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  keywords="Pranaam Hospitals Isnapur, hospital near Patancheru Hyderabad,
            multi specialty hospital Isnapur, emergency care Isnapur,
            best doctors near Patancheru, NABH hospital Isnapur,
            diagnostics Hyderabad"
  schema={isnapurSchema}
/>

    <div className="min-h-screen bg-white">
      <main>
        {/* Hero Section */}
        <section className="relative py-16 md:py-24 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div className="max-w-lg">
                <Badge className="mb-4 px-3 py-1 bg-primary/10 text-primary border-primary/20">
                  <Building className="w-4 h-4 mr-1" />
                  Pranaam Hospitals Branch
                </Badge>
                <h1 className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                  Isnapur <span className="text-primary">Branch</span>
                </h1>
                <p className="mb-8 text-lg text-gray-700">
                  Our Isnapur branch extends quality healthcare services to the Patancheru area with dedicated medical professionals and modern facilities.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button size="lg" className="gap-2 shadow-md" asChild>
                    <a
                        href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="mr-2 h-5 w-5" />
                        Book an Appointment
                      </a>
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary hover:text-white"
                    asChild
                  >
                    <Link to="tel:+917799818844">
                      <Phone className="w-5 h-5" />
                      Call Us
                    </Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-64 overflow-hidden rounded-lg shadow-xl md:h-96">
                  <img
                    src="/images/IsnapurHospitalBuilding.png"
                    alt="Pranaam Hospitals Isnapur Branch"
                    className="w-full h-full object-cover"
                  />
                 
                </div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-primary/10 rounded-full"></div>
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary/10 rounded-full"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Info Section */}
        <section className="py-8 bg-white">
          <div className="container px-4 mx-auto">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Location</h3>
                  </div>
                  <p className="text-gray-700">
                    9-40/3, Near Kotak Mahindra Bank, Muthangi Village, Patancheru Mandal, Sangareddy District – 502300
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Contact</h3>
                  </div>
                  <p className="text-gray-700">
                    <span className="block">Phone: +91 77998 18844</span>
                    <span className="block">Email: info@pranaamhospitals.in</span>
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Clock className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Hours</h3>
                  </div>
                  <p className="text-gray-700">
                    <span className="block">Emergency: 24/7</span>
                    <span className="block">OPD: 8:00 AM - 8:00 PM</span>
                  </p>
                </CardContent>
              </Card>

              <Card className="transition-all hover:shadow-md">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-2">
                    <div className="p-3 rounded-full bg-primary/10">
                      <Truck className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold">Emergency</h3>
                  </div>
                  <p className="text-gray-700">
                    <span className="block">24/7 Emergency Services</span>
                    <span className="block">Ambulance Available</span>
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* About Branch Section */}
        <section className="py-12 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="grid items-center grid-cols-1 gap-12 md:grid-cols-2">
              <div>
                <Badge className="mb-2">About Our Branch</Badge>
                <h2 className="mb-6 text-3xl font-bold md:text-4xl">
                  Accessible Healthcare at <span className="text-primary">Isnapur</span>
                </h2>
                <p className="mb-6 text-gray-700">
                  Our Isnapur branch brings quality healthcare closer to the communities in Patancheru and surrounding areas. We provide comprehensive medical services with a focus on accessibility and patient care.
                </p>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Community Focus</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Quality Care</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>24/7 Emergency</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary shrink-0" />
                    <span>Affordable Services</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="relative h-64 overflow-hidden rounded-lg shadow-xl md:h-96">
                  <img
                    src="/images/IsnapurHospitalBuilding.png"
                    alt="Isnapur Hospital Building"
                    className="w-full h-full object-cover"
                  />
                  
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Location Map */}
        <section className="py-12 bg-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center mb-12 text-center">
              <Badge className="mb-2">Find Us</Badge>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">
                Our <span className="text-primary">Location</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <div className="h-80 bg-gray-200 rounded-lg overflow-hidden">
                <iframe
                  src="https://www.google.com/maps?q=Isnapur,+Patancheru&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold">Pranaam Hospitals Isnapur</h3>
                  <p className="text-muted-foreground">
                    9-40/3, Near Kotak Mahindra Bank, Muthangi Village, Patancheru Mandal, Sangareddy District – 502300
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Phone className="w-4 h-4 text-primary" />
                    <span>Phone: +91 77998 18844</span>
                  </div>
                  <div className="mt-4">
                    <Button variant="outline" size="sm" className="gap-2" asChild>
                      <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">
                        <MapPin className="w-4 h-4" />
                        Get Directions
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="py-12 bg-primary text-white">
          <div className="container px-4 mx-auto">
            <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
              <div className="text-center md:text-left">
                <h2 className="mb-2 text-2xl font-bold md:text-3xl">Need Medical Assistance?</h2>
                <p className="text-primary-foreground/90">Schedule an appointment at our Isnapur branch today.</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button variant="secondary" size="lg" className="gap-2 shadow-md" asChild>
                  <a
                        href="https://pranaamhospitals.com/apps/pranaam/patient/?routeName=doctors&refId=otp&visitType=T&treatmentType=1&deepLink=Y&consultType=001"
                        rel="noopener noreferrer"
                      >
                        <Calendar className="mr-2 h-5 w-5" />
                        Book an Appointment
                      </a>
                </Button>
                <Button variant="secondary" size="lg" className="gap-2 shadow-md" asChild>
                  <Link to="tel:+917799818844">
                    <Phone className="w-5 h-5" />
                    Call Us Now
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
    
    </>
  )
}